// src/app/modules/demandes/DemanderFormationManager.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import "../../RessourcesStyles/DemanderFormation.css";

import UsersPickerModal from "./UsersPickerModal";
import BulkUsersFieldsModal from "./BulkUsersFieldsModal";
import HorsCatalogueManager from "./HorsCatalogueManager";

import { fetchVisibleCampagnesRequest } from "../../../../../redux/actions/campagnesActions";
import { fetchCataloguesRequest } from "../../../../../redux/actions/cataloguesActions";
import { fetchFormationsByCatalogueRequest } from "../../../../../redux/actions/formationsActions";
import { getDirectReportsRequest } from "../../../../../redux/actions/userActions";
import { createDemandeManagerBulkRequest } from "../../../../../redux/actions/demandesActions";

import {
    Search,
    Filter,
    ChevronDown,
    Eye,
    Download,
    BookOpen,
    Sparkles,
    Layers,
    Calendar,
    X,
} from "lucide-react";
import {API_BASE_URL} from "../../../../../../api";

const fmt = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return String(iso);
        return d.toLocaleString();
    } catch {
        return String(iso);
    }
};

export default function DemanderFormationManager({
                                                     campagneId,
                                                     hideCampagneSelect = false,
                                                     selectedCampagne = null,
                                                 }) {
    const dispatch = useDispatch();

    const campagnesState = useSelector((s) => s.campagnes || {});
    const cataloguesState = useSelector((s) => s.catalogues || {});
    const formationsState = useSelector((s) => s.formations || {});
    const usersState = useSelector((s) => s.user || {});

    const campagnes = campagnesState.visibleList || campagnesState.list || [];
    const cataloguesAll = cataloguesState.catalogues || cataloguesState.list || [];
    const { list: formations = [], loading: formationsLoading } = formationsState;
    const teamUsers = usersState.directReports || [];
    const currentUser = usersState.currentUser;

    const uid = sessionStorage.getItem("uid");

    // Campaign
    const [localCampagneId, setLocalCampagneId] = useState("");
    const campagneIdEff = hideCampagneSelect
        ? (campagneId ? Number(campagneId) : null)
        : (localCampagneId ? Number(localCampagneId) : null);

    // Tabs
    const [activeTab, setActiveTab] = useState("catalogues"); // catalogues | horsCatalogue | recommendations

    // Catalogues/formations state
    const [catalogueId, setCatalogueId] = useState("all");
    const [search, setSearch] = useState("");
    const [showFilters, setShowFilters] = useState(false);

    // Pagination formations
    const PAGE_SIZE = 9;
    const [page, setPage] = useState(1);

    // Formation details modal
    const [detailsFormation, setDetailsFormation] = useState(null);
    const [openDetails, setOpenDetails] = useState(false);

    // Bulk flow (Manager -> équipe)
    const [openUsersPick, setOpenUsersPick] = useState(false);
    const [openFieldsModal, setOpenFieldsModal] = useState(false);
    const [selectedFormation, setSelectedFormation] = useState(null);
    const [pickedUsers, setPickedUsers] = useState([]);

    useEffect(() => {
        dispatch(getDirectReportsRequest(uid));
        dispatch(fetchVisibleCampagnesRequest());
        dispatch(fetchCataloguesRequest());
    }, [dispatch, uid]);

    useEffect(() => {
        if (!hideCampagneSelect && !localCampagneId && campagnes?.length) {
            setLocalCampagneId(String(campagnes[0].id));
        }
    }, [hideCampagneSelect, campagnes, localCampagneId]);

    const campagneObj = useMemo(() => {
        if (selectedCampagne) return selectedCampagne;
        if (!campagneIdEff) return null;
        return (campagnes || []).find((c) => String(c.id) === String(campagneIdEff)) || null;
    }, [campagnes, campagneIdEff, selectedCampagne]);

    // reset view on campagne change
    useEffect(() => {
        setActiveTab("catalogues");
        setCatalogueId("all");
        setSearch("");
        setShowFilters(false);
        setPage(1);
    }, [campagneIdEff]);

    // catalogues visibles selon campagne.catalogueIds
    const visibleCatalogues = useMemo(() => {
        if (!campagneObj?.catalogueIds?.length) return [];
        const setIds = new Set(campagneObj.catalogueIds.map((x) => String(x)));
        return (cataloguesAll || []).filter((c) => setIds.has(String(c.id)));
    }, [cataloguesAll, campagneObj]);

    // Fetch formations by catalogue (backend)
    useEffect(() => {
        if (!campagneIdEff) return;
        if (catalogueId === "all") {
            dispatch(fetchFormationsByCatalogueRequest("all")); // saga => []
            return;
        }
        dispatch(fetchFormationsByCatalogueRequest(catalogueId));
    }, [dispatch, campagneIdEff, catalogueId]);

    const selectedCatalogue = useMemo(() => {
        if (catalogueId === "all") return null;
        return (visibleCatalogues || []).find((c) => String(c.id) === String(catalogueId)) || null;
    }, [visibleCatalogues, catalogueId]);

    const getCataloguePdfUrl = (cat) =>
        cat?.pdfUrl || cat?.pdfLink || cat?.cataloguePdfUrl || cat?.urlPdf || cat?.lienPdf || cat?.pdf || "";

    const getFormationImage = (f) =>
        f?.imageUrl || f?.image || f?.thumbnail || f?.picture || f?.photo || "";

    // Search filter (client-side)
    const filteredFormations = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return formations || [];
        return (formations || []).filter((f) => {
            const name = String(f?.nom || f?.name || "").toLowerCase();
            const ref = String(f?.reference || "").toLowerCase();
            return name.includes(q) || ref.includes(q);
        });
    }, [formations, search]);

    useEffect(() => setPage(1), [search, catalogueId]);

    const totalPages = useMemo(() => Math.max(1, Math.ceil((filteredFormations.length || 0) / PAGE_SIZE)), [filteredFormations.length]);
    const pageSafe = Math.min(Math.max(page, 1), totalPages);
    const pagedFormations = useMemo(() => {
        const start = (pageSafe - 1) * PAGE_SIZE;
        return filteredFormations.slice(start, start + PAGE_SIZE);
    }, [filteredFormations, pageSafe]);

    // -------- Bulk flow (Manager -> équipe) ----------
    const openBulkFlow = (formation) => {
        if (!campagneIdEff) return Swal.fire({ icon: "warning", text: "Sélectionne une campagne." });
        if (catalogueId === "all") return Swal.fire({ icon: "warning", text: "Choisis un catalogue." });

        setSelectedFormation(formation);
        setOpenUsersPick(true);
    };

    const onConfirmUsers = (ids) => {
        const uniqueIds = Array.from(new Set(ids || []));
        const users = (teamUsers || []).filter((u) => uniqueIds.includes(u.id));
        setPickedUsers(users);
        setOpenUsersPick(false);
        setOpenFieldsModal(true);
    };

    const onSubmitFields = (rows) => {
        if (!selectedFormation?.id) return Swal.fire({ icon: "warning", text: "Formation introuvable." });

        const payload = {
            campagneId: Number(campagneIdEff),
            type: "FORMATION",
            formationId: Number(selectedFormation.id),
            items: (rows || []).map((r) => ({
                targetUserId: Number(r.targetUserId),
                mode: r.mode || null,
                period: r.period || null,
                niveau: r.niveau || null,
                certifiante: !!r.certifiante,
                priorite: r.priorite != null ? Number(r.priorite) : null,
                objectifs: r.objectifs || null,
                objectifs2: r.objectifs2 || null,
                objectifs3: r.objectifs3 || null,
            })),
        };

        dispatch(createDemandeManagerBulkRequest(payload));
        setOpenFieldsModal(false);
        setPickedUsers([]);
        setSelectedFormation(null);
    };

    const openFormationDetails = (f) => {
        setDetailsFormation(f);
        setOpenDetails(true);
    };

    const loading = !!(campagnesState.loading || cataloguesState.loading || formationsLoading);

    return (
        <div className="dfPage">
            <div className="dfWrap">
                {/*<div className="dfHeader">*/}
                {/*    <h1>Demandes (Manager)</h1>*/}
                {/*    <p>*/}
                {/*        {currentUser ? (*/}
                {/*            <>*/}
                {/*                Manager : <b>{currentUser.firstName} {currentUser.lastName}</b>*/}
                {/*            </>*/}
                {/*        ) : "—"}*/}
                {/*    </p>*/}
                {/*</div>*/}

                <div className="dfCard">
                    {/* Step: Campagne */}
                    <div className="dfStepRow">
                        {!hideCampagneSelect && (
                            <div className="dfField">
                                <label>Campagne *</label>
                                <select value={localCampagneId} onChange={(e) => setLocalCampagneId(e.target.value)}>
                                    <option value="">— Sélectionner —</option>
                                    {(campagnes || []).map((c) => (
                                        <option key={c.id} value={c.id}>{c.nom || c.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {campagneIdEff ? (
                            <div className="dfStepHint">
                                Campagne sélectionnée : <b>{campagneObj?.nom || campagneObj?.name || "—"}</b>
                            </div>
                        ) : (
                            <div className="dfStepHint">Choisis une campagne pour afficher les onglets.</div>
                        )}
                    </div>

                    {/* Tabs */}
                    {campagneIdEff ? (
                        <div className="dfTabs">
                            <button
                                type="button"
                                className={`dfTab ${activeTab === "catalogues" ? "is-active" : ""}`}
                                onClick={() => setActiveTab("catalogues")}
                            >
                                <Layers size={16} />
                                Catalogues
                            </button>

                            <button
                                type="button"
                                className={`dfTab ${activeTab === "horsCatalogue" ? "is-active" : ""}`}
                                onClick={() => setActiveTab("horsCatalogue")}
                            >
                                <BookOpen size={16} />
                                Hors catalogue
                            </button>

                            <button
                                type="button"
                                className={`dfTab ${activeTab === "recommendations" ? "is-active" : ""}`}
                                onClick={() => setActiveTab("recommendations")}
                            >
                                <Sparkles size={16} />
                                Recommendations
                            </button>
                        </div>
                    ) : null}

                    {/* Content */}
                    {!campagneIdEff ? null : activeTab === "catalogues" ? (
                        <>
                            {/* Catalogues OR Formations */}
                            {catalogueId === "all" ? (
                                <>
                                    {loading ? (
                                        <div className="dfEmpty">Chargement...</div>
                                    ) : (visibleCatalogues || []).length === 0 ? (
                                        <div className="dfEmpty">Aucun catalogue disponible pour cette campagne.</div>
                                    ) : (
                                        <div className="dfCatalogueGrid">
                                            {(visibleCatalogues || []).map((cat) => {
                                                const pdfUrl = getCataloguePdfUrl(cat);
                                                return (
                                                    <div className="dfCatalogueCard" key={cat.id}>
                                                        <div className="dfCatalogueTitle">{cat.nom || cat.name || "Catalogue"}</div>
                                                        <div className="dfCatalogueSub">ID: {cat.id}</div>

                                                        <div className="dfCatalogueActions">
                                                            <button
                                                                type="button"
                                                                className="dfIconBtn"
                                                                title="Consulter"
                                                                onClick={() => setCatalogueId(String(cat.id))}
                                                            >
                                                                <Eye size={16} />
                                                            </button>

                                                            <a
                                                                className={`dfIconBtn ${pdfUrl ? "" : "is-disabled"}`}
                                                                title={pdfUrl ? "Télécharger le catalogue (PDF)" : "Lien PDF indisponible"}
                                                                href={pdfUrl || undefined}
                                                                target={pdfUrl ? "_blank" : undefined}
                                                                rel={pdfUrl ? "noreferrer" : undefined}
                                                                onClick={(e) => { if (!pdfUrl) e.preventDefault(); }}
                                                            >
                                                                <Download size={16} />
                                                            </a>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    {/* Formations */}
                                    <div className="dfFormationsHeader">
                                        <div>
                                            <div className="dfSectionTitle">Formations</div>
                                            <div className="dfSectionSub">
                                                Catalogue : <b>{selectedCatalogue?.nom || selectedCatalogue?.name || catalogueId}</b>
                                            </div>
                                        </div>

                                        <button type="button" className="dfLightBtn" onClick={() => setCatalogueId("all")}>
                                            Retour catalogues
                                        </button>
                                    </div>

                                    <div className="dfControls">
                                        <div className="dfSearch">
                                            <Search size={16} />
                                            <input
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                                placeholder="Rechercher une formation (nom, référence...)"
                                            />
                                        </div>

                                        {/*<button type="button" className="dfFilterBtn" onClick={() => setShowFilters((v) => !v)}>*/}
                                        {/*    <Filter size={16} />*/}
                                        {/*    Options*/}
                                        {/*    <ChevronDown size={16} className={`dfChevron ${showFilters ? "is-open" : ""}`} />*/}
                                        {/*</button>*/}
                                    </div>

                                    {showFilters && (
                                        <div className="dfFilters">
                                            <div className="dfField">
                                                <label>Période (info)</label>
                                                <div className="dfInlineInfo">
                                                    <Calendar size={16} />
                                                    <span className="dfMuted">{campagneObj?.period || "—"}</span>
                                                </div>
                                            </div>
                                            <div className="dfField">
                                                <label>Réinitialiser</label>
                                                <button type="button" className="dfLightBtn" onClick={() => setSearch("")}>
                                                    Réinitialiser
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {loading ? (
                                        <div className="dfEmpty">Chargement...</div>
                                    ) : filteredFormations.length === 0 ? (
                                        <div className="dfEmpty">Aucune formation trouvée.</div>
                                    ) : (
                                        <>
                                            <div className="dfFormationGrid">
                                                {pagedFormations.map((f) => {
                                                    const img = getFormationImage(f);
                                                    return (
                                                        <div className="dfFormationCard" key={f.id}>
                                                            <div className="dfFormationMedia">
                                                                {f.picture ? (
                                                                    <img
                                                                        className="pdt-thumb"
                                                                        src={`${API_BASE_URL}/api/formations/images/${f.picture}`}
                                                                        alt="domaine"
                                                                        // onError={(e) => (e.currentTarget.style.display = "none")}
                                                                    />
                                                                ) : (
                                                                    <div className="dfFormationPlaceholder">
                                                                        {(f.nom || f.name || "F").slice(0, 1).toUpperCase()}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className="dfFormationBody">
                                                                <div className="dfFormationTitle" title={f.nom || f.name || ""}>
                                                                    {f.nom || f.name || "—"}
                                                                </div>

                                                                <div className="dfFormationDesc">
                                                                    {f.description || f.desc || "—"}
                                                                </div>

                                                                <div className="dfFormationMeta">
                                                                    {f.reference ? <span className="dfPill">Ref: {f.reference}</span> : null}
                                                                    {f.duree ? <span className="dfPill">{f.duree} j</span> : null}
                                                                </div>

                                                                <div className="dfFormationActions">
                                                                    <button
                                                                        type="button"
                                                                        className="dfIconBtn"
                                                                        title="Voir les détails"
                                                                        onClick={() => openFormationDetails(f)}
                                                                    >
                                                                        <Eye size={16} />
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        className="dfPrimaryBtn"
                                                                        onClick={() => openBulkFlow(f)}
                                                                    >
                                                                        Demander
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            <div className="dfPagination">
                                                <button
                                                    type="button"
                                                    className="dfLightBtn"
                                                    disabled={pageSafe <= 1}
                                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                                >
                                                    Précédent
                                                </button>

                                                <div className="dfPageInfo">
                                                    Page <b>{pageSafe}</b> / {totalPages} —{" "}
                                                    <span className="dfMuted">{filteredFormations.length} formations</span>
                                                </div>

                                                <button
                                                    type="button"
                                                    className="dfLightBtn"
                                                    disabled={pageSafe >= totalPages}
                                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                                >
                                                    Suivant
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    ) : activeTab === "horsCatalogue" ? (
                        <div style={{ marginTop: 10 }}>
                            <HorsCatalogueManager campagneId={campagneIdEff} hideCampagneSelect />
                        </div>
                    ) : (
                        <div className="dfEmpty">⏳ Cet onglet sera traité après. (Recommendations)</div>
                    )}
                </div>
            </div>

            {/* ===== Formation Details Modal ===== */}
            {openDetails && detailsFormation && (
                <div className="dfModal" onMouseDown={(e) => e.target === e.currentTarget && setOpenDetails(false)}>
                    <div className="dfModal__panel">
                        <div className="dfModal__header">
                            <div className="dfModal__headLeft">
                                <div className="dfModal__icon">
                                    <Eye size={18} />
                                </div>
                                <div>
                                    <div className="dfModal__title">{detailsFormation.nom || detailsFormation.name || "Détails formation"}</div>
                                    <div className="dfModal__sub">ID: {detailsFormation.id}</div>
                                </div>
                            </div>

                            <button type="button" className="dfIconBtn" onClick={() => setOpenDetails(false)} title="Fermer">
                                <X size={16} />
                            </button>
                        </div>

                        <div className="dfModal__body">
                            <div className="dfPanel">
                                <div className="dfPanel__title">Informations</div>

                                <div className="dfGrid2">
                                    <div>
                                        <div className="dfInfo__label">Nom</div>
                                        <div className="dfInfo__value">{detailsFormation.nom || detailsFormation.name || "—"}</div>
                                    </div>

                                    <div>
                                        <div className="dfInfo__label">Référence</div>
                                        <div className="dfInfo__value">{detailsFormation.reference || "—"}</div>
                                    </div>

                                    <div>
                                        <div className="dfInfo__label">Durée</div>
                                        <div className="dfInfo__value">{detailsFormation.duree ? `${detailsFormation.duree} j` : "—"}</div>
                                    </div>

                                    <div>
                                        <div className="dfInfo__label">Catalogue</div>
                                        <div className="dfInfo__value">{selectedCatalogue?.nom || selectedCatalogue?.name || "—"}</div>
                                    </div>
                                </div>

                                <div style={{ marginTop: 12 }}>
                                    <div className="dfInfo__label">Description</div>
                                    <div className="dfInfo__value" style={{ fontWeight: 500, color: "#374151" }}>
                                        {detailsFormation.description || detailsFormation.desc || "—"}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="dfModal__footer">
                            <button type="button" className="dfLightBtn" onClick={() => setOpenDetails(false)}>
                                Fermer
                            </button>
                            <button
                                type="button"
                                className="dfPrimaryBtn"
                                onClick={() => {
                                    setOpenDetails(false);
                                    openBulkFlow(detailsFormation);
                                }}
                            >
                                Demander
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== Bulk modals ===== */}
            <UsersPickerModal
                open={openUsersPick}
                onClose={() => setOpenUsersPick(false)}
                users={teamUsers}
                title="Sélectionner des collaborateurs (équipe)"
                onConfirm={onConfirmUsers}
            />

            <BulkUsersFieldsModal
                open={openFieldsModal}
                onClose={() => setOpenFieldsModal(false)}
                users={pickedUsers}
                title="Champs par collaborateur (Manager)"
                onSubmit={onSubmitFields}
            />
        </div>
    );
}
